import { motion } from 'framer-motion';
import { BookOpen, Star, Clock, Heart, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';

const BOOK_COVER = '/marketing/book-cover-marketing.jpg';

const fadeUp = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: '-80px' },
  transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1] },
};

export default function ComingSoonPage() {
  return (
    <div data-testid="coming-soon-page">
      {/* Page Header */}
      <section className="py-20 md:py-28 bg-[#FDFBF7]">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <motion.div {...fadeUp}>
            <p className="font-sans text-xs tracking-[0.3em] uppercase text-[#A67A5B] mb-4">
              On The Horizon
            </p>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-semibold text-[#2A2421] mb-6 leading-tight">
              Coming Soon
            </h1>
            <p className="font-sans text-base md:text-lg text-[#7A736E] max-w-2xl mx-auto leading-relaxed">
              More faith-filled projects are on the way. Each one carries a testimony, a purpose, and a message that the world needs to hear.
            </p>
          </motion.div>
        </div>
      </section>

      {/* ===== BOOK PART ONE ===== */}
      <section className="py-20 md:py-28 bg-[#2A2421] text-white relative overflow-hidden grain-overlay" data-testid="coming-soon-book-one">
        <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 md:gap-16 items-center">
            {/* Book Cover */}
            <motion.div {...fadeUp} className="flex justify-center">
              <div className="relative">
                <div className="absolute -inset-4 bg-gradient-to-br from-[#C59B76]/15 to-transparent rounded-3xl" />
                <img
                  src={BOOK_COVER}
                  alt="He Tried to Kill Me But God Kept Me Alive - by Alzadia L. Spires"
                  data-testid="coming-soon-book-one-cover"
                  className="relative rounded-2xl shadow-2xl w-full max-w-[600px] book-cover-image"
                />
              </div>
            </motion.div>

            {/* Book Info */}
            <motion.div {...fadeUp}>
              <div className="flex items-center gap-3 mb-6">
                <span className="inline-block border border-[#C59B76]/50 text-[#C59B76] text-xs font-semibold tracking-[0.2em] uppercase px-4 py-1.5 rounded-full">
                  Book One
                </span>
                <span className="inline-block bg-[#C59B76]/20 text-[#C59B76] text-xs font-semibold tracking-[0.15em] uppercase px-3 py-1 rounded-full">
                  Coming Soon
                </span>
              </div>
              <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-semibold text-white mb-3 leading-tight">
                He Tried to Kill Me, But God Kept Me Alive
              </h2>
              <p className="font-serif text-lg md:text-xl text-[#C59B76] italic mb-6">
                A Mother's Survival Story
              </p>
              <p className="font-sans text-base text-white/60 leading-relaxed mb-4">
                Based on a true story. She thought she was building a life. What she didn't know was that everything was being used against her. Behind closed doors, control turned into fear and fear turned into survival.
              </p>
              <p className="font-sans text-sm text-white/45 leading-relaxed mb-6">
                Her journey from manipulation, betrayal, and life-threatening danger to unwavering faith will leave you in awe of God's power and grace. A powerful true story of faith, resilience, and God's protection through life's darkest storms.
              </p>

              {/* WHY THIS BOOK FIRST — foundation callout */}
              <div
                className="border-l-[3px] border-[#C59B76] pl-5 py-1 mb-8"
                data-testid="before-the-storm-callout"
              >
                <p className="font-sans text-[10px] tracking-[0.28em] uppercase text-[#C59B76] font-semibold mb-3">
                  Before the Storm &middot; Why This Book First
                </p>
                <p className="font-serif text-base md:text-[17px] italic text-white/75 leading-relaxed">
                  Long before a son fought for his life, God was preparing a mother to fight for him. This book is not a detour on the way to Jarvis&rsquo;s story &mdash; it is the ground his story grows from. Through abuse, betrayal, divorce, and survival, faith was being built in the dark. Read this first, and you will understand why nothing about what comes next was ever by accident.
                </p>
              </div>

              <p className="font-sans text-xs text-white/30 italic mb-8">
                By Alzadia L. Spires &middot; Published by Cypress Admin Studio
              </p>
              <div className="flex items-center gap-2 text-[#C59B76]/60">
                <Clock size={16} />
                <span className="text-sm font-sans">Release date coming soon</span>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Bridge Text */}
      <section className="py-16 md:py-20 bg-[#FDFBF7]">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <motion.div {...fadeUp}>
            <div className="w-12 h-[1px] bg-[#A67A5B] mx-auto mb-8" />
            <p className="font-serif text-lg md:text-xl italic text-[#2A2421]/60 leading-relaxed">
              "From a mother's fight for survival to a son's miraculous journey through life-threatening trials, this story is still unfolding."
            </p>
            <div className="flex items-center justify-center gap-4 mt-8">
              <div className="w-8 h-[1px] bg-[#A67A5B]/40" />
              <Heart className="text-[#A67A5B]" size={16} />
              <div className="w-8 h-[1px] bg-[#A67A5B]/40" />
            </div>
          </motion.div>
        </div>
      </section>

      {/* ===== BOOK PART TWO - JARVIS'S JOURNEY ===== */}
      <section className="py-20 md:py-28 bg-[#F5F2EB]" data-testid="coming-soon-jarvis">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 md:gap-20 items-center">
            {/* Placeholder Image */}
            <motion.div {...fadeUp} className="flex justify-center md:justify-end">
              <div className="relative w-full max-w-[360px]">
                <div className="aspect-[3/4] rounded-2xl border-2 border-dashed border-[#A67A5B]/25 bg-white/70 flex flex-col items-center justify-center p-10 text-center shadow-lg">
                  <div className="w-20 h-20 rounded-full bg-[#A67A5B]/10 flex items-center justify-center mb-8">
                    <Star className="text-[#A67A5B]" size={32} />
                  </div>
                  <p className="font-serif text-xl font-semibold text-[#2A2421] mb-1">
                    Miracle So Great
                  </p>
                  <p className="font-serif text-base text-[#A67A5B] italic mb-4">
                    Cover Reveal Coming Soon
                  </p>
                  <div className="w-16 h-[1px] bg-[#A67A5B]/30" />
                </div>
              </div>
            </motion.div>

            {/* Jarvis Info */}
            <motion.div {...fadeUp}>
              <div className="flex items-center gap-3 mb-6">
                <span className="inline-block border border-[#A67A5B]/40 text-[#A67A5B] text-xs font-semibold tracking-[0.2em] uppercase px-4 py-1.5 rounded-full">
                  Part Two
                </span>
                <span className="inline-block bg-[#A67A5B]/10 text-[#A67A5B] text-xs font-semibold tracking-[0.15em] uppercase px-3 py-1 rounded-full">
                  Coming Soon
                </span>
              </div>
              <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-semibold text-[#2A2421] mb-2 leading-tight">
                Jarvis Spires: Miracle So Great
              </h2>
              <p className="font-serif text-lg md:text-xl text-[#A67A5B] italic mb-6">
                A True Story of Faith, Survival, and Rebirth
              </p>
              <p className="font-sans text-base text-[#7A736E] leading-relaxed mb-4">
                A story of survival. A story of faith. A story the world needs to hear. Born from the same fire that forged a mother's testimony, this is the next chapter &mdash; centered on resilience, restoration, and the undeniable hand of God in the middle of life's darkest moments.
              </p>
              <p className="font-sans text-sm text-[#7A736E]/80 leading-relaxed mb-4">
                This is more than a story of hardship. It is a story of miracle, courage, and purpose &mdash; a testimony that carries the power of God's faithfulness from one generation to the next.
              </p>
              <p className="font-sans text-sm text-[#7A736E]/60 italic leading-relaxed mb-8">
                Book Two in a powerful true-story series of faith, survival, and rebirth.
              </p>
              <Link
                to="/contact"
                data-testid="jarvis-stay-updated-btn"
                className="inline-flex items-center justify-center gap-2 bg-[#A67A5B] text-white px-8 py-4 rounded-full font-medium tracking-wide shadow-md hover:bg-[#8C654A] hover:shadow-lg hover:-translate-y-0.5 transition-all duration-300"
              >
                <Clock size={18} />
                Stay Updated
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* More Coming */}
      <section className="py-20 md:py-28 bg-white" data-testid="coming-soon-more">
        <div className="max-w-5xl mx-auto px-6 md:px-12">
          <motion.div {...fadeUp} className="text-center mb-16">
            <h2 className="font-serif text-3xl sm:text-4xl font-semibold text-[#2A2421] mb-4">
              Books &amp; Projects
            </h2>
            <p className="font-sans text-base text-[#7A736E] max-w-xl mx-auto">
              More faith-filled books, journals, devotionals, and projects are in the works. Each one carries a testimony and a purpose.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { title: 'Devotional Series', subtitle: 'Daily faith-building devotionals' },
              { title: 'Children\'s Book', subtitle: 'Faith stories for young hearts' },
              { title: 'Speaking & Events', subtitle: 'Sharing testimony and encouragement' },
            ].map((project, i) => (
              <motion.div
                key={project.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.12, duration: 0.6 }}
                className="p-8 rounded-2xl border-2 border-dashed border-[#E8E1D9] bg-[#FDFBF7]/50 text-center"
                data-testid={`future-project-${i}`}
              >
                <div className="w-14 h-14 rounded-xl bg-[#A67A5B]/10 flex items-center justify-center mx-auto mb-5">
                  <BookOpen className="text-[#A67A5B]" size={24} />
                </div>
                <h3 className="font-serif text-lg font-semibold text-[#2A2421] mb-2">
                  {project.title}
                </h3>
                <p className="font-sans text-sm text-[#7A736E]">{project.subtitle}</p>
                <p className="font-sans text-xs text-[#A67A5B] mt-3 font-medium">Coming Soon</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
